import { useEffect, useState } from 'react'

import { api, ErrorApi, type ConsumoDelLead, type MesConsumo, type ProyeccionCliente } from '../api'
import { fechaCorta, leerNumero } from '../formato'
import { ProyeccionConsumo } from './ProyeccionConsumo'

/**
 * El historial de planillas del cliente: se escribe a mano, mes a mes, y se proyecta con
 * el motor. El consumo que dejo el lead se OFRECE (es un numero sin mes): el asesor elige
 * en que mes va. Copiarlo solo seria inventarle una fecha.
 */

interface Fila {
  periodo: string
  kwh: string
  demanda: string
  origen: 'manual' | 'lead'
}

const aFila = (m: MesConsumo): Fila => ({ periodo: m.periodo, kwh: String(m.kwh), demanda: m.demanda_kw === null ? '' : String(m.demanda_kw), origen: m.origen })

function mesSiguiente(periodo: string): string {
  const [a, m] = periodo.split('-').map(Number) as [number, number]
  return m === 12 ? `${a + 1}-01` : `${a}-${String(m + 1).padStart(2, '0')}`
}

function mesAnterior(periodo: string): string {
  const [a, m] = periodo.split('-').map(Number) as [number, number]
  return m === 1 ? `${a - 1}-12` : `${a}-${String(m - 1).padStart(2, '0')}`
}

const esteMes = () => new Date().toISOString().slice(0, 7)

export function HistorialConsumo({ clienteId, conDemanda }: { clienteId: string; conDemanda: boolean }) {
  const [filas, setFilas] = useState<Fila[]>([])
  const [lead, setLead] = useState<ConsumoDelLead | null>(null)
  const [mesDelLead, setMesDelLead] = useState(mesAnterior(esteMes()))
  const [cambios, setCambios] = useState(false)
  const [proyeccion, setProyeccion] = useState<ProyeccionCliente | null>(null)
  const [error, setError] = useState('')
  const [ocupado, setOcupado] = useState<'' | 'guardando' | 'proyectando'>('')

  useEffect(() => {
    api
      .consumos(clienteId)
      .then(({ consumos, lead }) => {
        setFilas(consumos.map(aFila))
        setLead(lead)
      })
      .catch((err) => setError(err instanceof ErrorApi ? err.message : 'No se pudo cargar el historial.'))
  }, [clienteId])

  const cambiar = (nuevas: Fila[]) => {
    setFilas(nuevas)
    setCambios(true)
    setProyeccion(null)
  }
  const fijar = (i: number, campo: keyof Fila, valor: string) => cambiar(filas.map((f, j) => (j === i ? { ...f, [campo]: valor } : f)))

  /** Hacia ATRAS desde el primer mes (o desde el mes pasado): la planilla trae los ultimos meses. */
  const anadirAnteriores = (cuantos: number) => {
    const nuevas: Fila[] = []
    let periodo = filas.length ? filas[0]!.periodo : esteMes()
    for (let i = 0; i < cuantos; i++) {
      periodo = mesAnterior(periodo)
      nuevas.unshift({ periodo, kwh: '', demanda: '', origen: 'manual' })
    }
    cambiar([...nuevas, ...filas])
  }

  const anadirSiguiente = () => {
    const periodo = filas.length ? mesSiguiente(filas[filas.length - 1]!.periodo) : mesAnterior(esteMes())
    cambiar([...filas, { periodo, kwh: '', demanda: '', origen: 'manual' }])
  }

  const guardar = async () => {
    const meses: MesConsumo[] = []
    for (const f of filas) {
      const kwh = leerNumero(f.kwh)
      const demanda = leerNumero(f.demanda)
      if (!/^[0-9]{4}-[0-9]{2}$/.test(f.periodo)) return setError('Cada fila necesita su mes.')
      if (kwh === null || !(kwh > 0)) return setError(`Escribe los kWh de ${f.periodo} o quita la fila (un mes en 0 no se guarda).`)
      if (Number.isNaN(demanda) || (demanda !== null && !(demanda > 0))) return setError(`La demanda de ${f.periodo} no es válida.`)
      meses.push({ periodo: f.periodo, kwh, demanda_kw: demanda, origen: f.origen })
    }
    setError('')
    setOcupado('guardando')
    try {
      const { consumos } = await api.guardarConsumos(clienteId, meses)
      setFilas(consumos.map(aFila))
      setCambios(false)
    } catch (err) {
      setError(err instanceof ErrorApi ? err.message : 'No se pudo guardar.')
    } finally {
      setOcupado('')
    }
  }

  const proyectar = async () => {
    setError('')
    setOcupado('proyectando')
    try {
      setProyeccion(await api.proyectar(clienteId))
    } catch (err) {
      setError(err instanceof ErrorApi ? err.message : 'No se pudo proyectar.')
    } finally {
      setOcupado('')
    }
  }

  const leadYaUsado = filas.some((f) => f.origen === 'lead')
  const mostrarDemanda = conDemanda || filas.some((f) => f.demanda !== '')

  return (
    <div className="tarjeta historial">
      {lead && !leadYaUsado && (
        <div className="bloque-descuento abierto lead-sugerido">
          <span>
            El lead ({lead.canal === 'web' ? 'formulario web' : 'WhatsApp'}, {fechaCorta(lead.fecha)}) dejó <strong>{lead.kwh.toLocaleString('es-EC')} kWh/mes</strong>, sin mes de planilla.
          </span>
          <label className="en-linea">
            ¿De qué mes es?
            <input type="month" value={mesDelLead} onChange={(e) => setMesDelLead(e.target.value)} />
          </label>
          <button
            type="button"
            className="secundario pequeno"
            disabled={!mesDelLead || filas.some((f) => f.periodo === mesDelLead)}
            onClick={() => cambiar([...filas, { periodo: mesDelLead, kwh: String(lead.kwh), demanda: '', origen: 'lead' as const }].sort((a, b) => a.periodo.localeCompare(b.periodo)))}
          >
            Añadir al historial
          </button>
        </div>
      )}

      {filas.length > 0 && (
        <div className="tabla-envoltura">
          <table className="tabla compacta historial-tabla">
            <thead>
              <tr>
                <th>Mes</th>
                <th>kWh</th>
                {mostrarDemanda && <th>Demanda (kW)</th>}
                <th />
              </tr>
            </thead>
            <tbody>
              {filas.map((f, i) => (
                <tr key={i} className="sin-clic">
                  <td>
                    <input type="month" aria-label="Mes" value={f.periodo} onChange={(e) => fijar(i, 'periodo', e.target.value)} />
                  </td>
                  <td>
                    <input aria-label={`kWh ${f.periodo}`} value={f.kwh} onChange={(e) => fijar(i, 'kwh', e.target.value)} inputMode="decimal" />
                    {f.origen === 'lead' && <span className="insignia insignia-web">del lead</span>}
                  </td>
                  {mostrarDemanda && (
                    <td>
                      <input aria-label={`kW ${f.periodo}`} value={f.demanda} onChange={(e) => fijar(i, 'demanda', e.target.value)} inputMode="decimal" />
                    </td>
                  )}
                  <td className="accion">
                    <button type="button" className="enlace" aria-label={`Quitar ${f.periodo}`} onClick={() => cambiar(filas.filter((_, j) => j !== i))}>
                      Quitar
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="botones izquierda">
        <button type="button" className="secundario pequeno" onClick={() => anadirAnteriores(12)}>
          + 12 meses anteriores
        </button>
        <button type="button" className="secundario pequeno" onClick={() => anadirAnteriores(1)}>
          + Mes anterior
        </button>
        <button type="button" className="secundario pequeno" onClick={anadirSiguiente}>
          + Mes siguiente
        </button>
        <span className="suave pequeno-texto">{filas.length} meses{cambios ? ' · sin guardar' : ''}</span>
        <span className="espaciador" />
        <button type="button" className="secundario" onClick={guardar} disabled={!cambios || ocupado !== ''}>
          {ocupado === 'guardando' ? 'Guardando…' : 'Guardar historial'}
        </button>
        <button type="button" className="primario" onClick={proyectar} disabled={cambios || filas.length === 0 || ocupado !== ''}>
          {ocupado === 'proyectando' ? 'Proyectando…' : 'Proyectar 12 meses'}
        </button>
      </div>
      {error && <p className="error">{error}</p>}

      {proyeccion && (
        <>
          <h3>Consumo (kWh)</h3>
          <ProyeccionConsumo proyeccion={proyeccion.proyeccion.kwh} historial={proyeccion.historial.kwh} unidad="kWh" />
          {proyeccion.proyeccion.demanda_kw && (
            <>
              <h3>Demanda (kW)</h3>
              <ProyeccionConsumo proyeccion={proyeccion.proyeccion.demanda_kw} historial={proyeccion.historial.demanda_kw ?? []} unidad="kW" />
            </>
          )}
        </>
      )}
    </div>
  )
}
